<template>
  <div class="profile-settings">
    <div class="profile-card">
      <div class="profile-header">
        <div class="avatar-circle">
          <ImagePreview
            v-if="userStore.userInfo?.avatar"
            :src="userStore.userInfo.avatar"
            :alt="userStore.userInfo?.name || ''"
            width="100%"
            height="100%"
            fit="cover"
            :enable-preview="true"
            :round="true"
          />
          <span v-else>{{ userStore.userInfo?.name?.charAt(0)?.toUpperCase() || userStore.userInfo?.username?.charAt(0)?.toUpperCase() || '' }}</span>
        </div>
        <div class="profile-info">
          <div class="profile-name">{{ userStore.userInfo?.name || userStore.userInfo?.username || '' }}</div>
          <div class="profile-phone">{{ userStore.userInfo?.phone || userStore.userInfo?.phoneNumber || 'N/A' }}</div>
        </div>
      </div>
    </div>

    <div class="settings-card">
      <div class="card-section-title">{{ $t('settings.accountSettings') }}</div>
      <div class="menu-list">
        <div class="menu-item" @click="goToChangePassword">
          <div class="menu-left">
            <el-icon class="menu-icon"><Lock /></el-icon>
            <span class="menu-label">{{ $t('profile.changePassword') }}</span>
          </div>
          <el-icon class="menu-arrow"><ArrowRight /></el-icon>
        </div>
        <div v-if="false" class="menu-item" @click="handleWeChatBinding">
          <div class="menu-left">
            <el-icon class="menu-icon"><ChatDotRound /></el-icon>
            <span class="menu-label">{{ $t('profile.wechatBinding') }}</span>
          </div>
          <div class="menu-right">
            <el-tag :type="wechatBound ? 'success' : 'info'" size="small">
              {{ wechatBound ? $t('profile.bound') : $t('profile.unbound') }}
            </el-tag>
            <el-icon class="menu-arrow"><ArrowRight /></el-icon>
          </div>
        </div>
        <div v-if="false" class="menu-item" @click="handleNotification">
          <div class="menu-left">
            <el-icon class="menu-icon"><Bell /></el-icon>
            <span class="menu-label">{{ $t('profile.notificationSettings') }}</span>
          </div>
          <el-icon class="menu-arrow"><ArrowRight /></el-icon>
        </div>
      </div>
    </div>


    <div class="logout-section">
      <el-button type="danger" class="logout-btn" @click="handleLogout">
        {{ $t('common.logout') }}
      </el-button>
    </div>
    <el-dialog 
      v-model="showWeChatDialog" 
      :title="$t('profile.wechatBinding')"
      width="420px"
      :close-on-click-modal="true"
      @close="stopPolling"
      center
    >
      <div class="wechat-dialog-content">
        <div v-if="loadingQR" class="loading-container">
          <el-icon class="loading-icon"><Loading /></el-icon>
          <span>{{ $t('profile.generatingQR') }}</span>
        </div>
        <div v-else-if="qrCodeUrl" class="qr-container">
          <img :src="qrCodeUrl" alt="WeChat QR Code" class="qr-code" />
          <div class="qr-tip">{{ $t('profile.scanTip') }}</div>
        </div>
        <div v-else class="error-container">
          <el-icon class="error-icon"><WarningFilled /></el-icon>
          <span>{{ $t('profile.qrFailed') }}</span>
          <el-button type="primary" size="small" @click="generateQRCode" class="retry-btn">
            {{ $t('common.retry') }}
          </el-button>
        </div>
        
        <div v-if="bindSuccess" class="bind-success">
          <el-icon class="success-icon"><CircleCheckFilled /></el-icon>
          <span>{{ $t('profile.bindSuccess') }}</span>
        </div>
      </div>
      <template #footer>
        <el-button @click="showWeChatDialog = false">{{ $t('common.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Lock, Bell, Document, ArrowRight, ChatDotRound, Loading, WarningFilled, CircleCheckFilled } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'
import { getWeChatQRCode, getWeChatStatus } from '@/api/wechat'
import ImagePreview from '@/components/ImagePreview.vue'

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const showWeChatDialog = ref(false)
const loadingQR = ref(false)
const qrCodeUrl = ref('')
const sceneStr = ref('')
const pollingTimer = ref(null)
const bindSuccess = ref(false)
const wechatBound = ref(false)

const formatRole = (role) => {
  if (Array.isArray(role)) {
    role = role[0]
  }
  const roleMap = {
    'admin': 'Administrator',
    'teacher': 'Teacher',
    'user': 'User',
    'coach': 'Coach'
  }
  return roleMap[role] || role || 'User'
}

// Fetch profile when component mounts
onMounted(async () => {
  console.log('📄 ProfileSettings mounted - fetching user profile')
  await userStore.fetchUserProfile()
})

const generateQRCode = async () => {
  loadingQR.value = true
  qrCodeUrl.value = ''
  bindSuccess.value = false
  
  const currentOpenid = userStore.userOpenid
  if (!currentOpenid) {
    ElMessage.error('User openid not found, please login again')
    loadingQR.value = false
    return
  }
  
  sceneStr.value = currentOpenid
  
  try {
    const res = await getWeChatQRCode(sceneStr.value, 2)
    if (res.success && res.data) {
      if (res.data.message === 'Already subscribed') {
        ElMessage.warning(res.data.message)
        qrCodeUrl.value = ''
      } else {
        qrCodeUrl.value = res.data
        startPolling()
      }
    } else if (res.data && res.data.message) {
      ElMessage.warning(res.data.message)
      qrCodeUrl.value = ''
    } else {
      ElMessage.error(res.message || 'Failed to generate QR code')
    }
  } catch (error) {
    console.error('Failed to generate QR code:', error)
    ElMessage.error('Failed to generate QR code')
  } finally {
    loadingQR.value = false
  }
}

const startPolling = () => {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value)
  }
  
  pollingTimer.value = setInterval(async () => {
    if (!sceneStr.value || bindSuccess.value) return
    
    try {
      const res = await getWeChatStatus(sceneStr.value)
      if (res.success && res.data === 'SUCCESS') {
        bindSuccess.value = true
        wechatBound.value = true
        ElMessage.success('WeChat bound successfully!')
        setTimeout(() => {
          showWeChatDialog.value = false
        }, 1500)
      }
    } catch (error) {
      console.error('Failed to check status:', error)
    }
  }, 2000)
}

const stopPolling = () => {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value)
    pollingTimer.value = null
  }
}

const goToChangePassword = () => {
  router.push('/profile/change-password')
}

const handleNotification = () => {
  ElMessage.info('Notification settings feature coming soon')
}

const handleFeedback = () => {
  ElMessage.info('Feedback feature coming soon')
}

const handleLogout = async () => {
  try {
    await ElMessageBox.confirm(
      t('common.logoutConfirm'),
      t('common.logout'),
      {
        confirmButtonText: t('common.logout'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )
    
    userStore.clearSession()
    router.push('/login')
    ElMessage.success(t('common.logoutSuccess'))
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Logout error:', error)
    }
  }
}

const handleWeChatBinding = async () => {
  loadingQR.value = true
  
  const currentOpenid = userStore.userOpenid
  if (!currentOpenid) {
    ElMessage.error('User openid not found, please login again')
    loadingQR.value = false
    return
  }
  
  sceneStr.value = currentOpenid
  
  try {
    const res = await getWeChatQRCode(sceneStr.value, 2)
    if (res.success && res.data) {
      if (res.data.message === 'Already subscribed') {
        ElMessage.warning(res.data.message)
      } else {
        showWeChatDialog.value = true
        qrCodeUrl.value = res.data
        startPolling()
      }
    } else if (res.data && res.data.message) {
      ElMessage.warning(res.data.message)
    } else {
      ElMessage.error(res.message || 'Failed to generate QR code')
    }
  } catch (error) {
    console.error('Failed to generate QR code:', error)
    ElMessage.error('Failed to generate QR code')
  } finally {
    loadingQR.value = false
  }
}

onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped lang="scss">
.profile-settings {
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  
  .profile-card {
    background: var(--bg-color-white, #ffffff);
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 16px;
    transition: background-color 0.3s ease;

    @media (max-width: 768px) {
      padding: 16px;
      margin-bottom: 12px;
      border-radius: 10px;
    }
    
    @media (max-width: 480px) {
      padding: 14px;
      margin-bottom: 10px;
      border-radius: 8px;
    }
    
    html.dark & {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
    }

    .profile-header {
      display: flex;
      align-items: center;
      gap: 16px;
            
      @media (max-width: 480px) {
        flex-direction: column;
        text-align: center;
        gap: 12px;
      }

      .avatar-circle {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 36px;
        font-weight: 600;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
        overflow: hidden;
        border: 3px solid #ffffff;
        cursor: pointer;
        transition: all 0.3s ease;
        
        @media (max-width: 768px) {
          width: 70px;
          height: 70px;
          font-size: 32px;
        }
        
        @media (max-width: 480px) {
          width: 60px;
          height: 60px;
          font-size: 26px;
        }
        
        html.dark & {
          box-shadow: 0 4px 12px rgba(64, 158, 255, 0.5);
          border-color: var(--bg-color-dark, #1a1a1a);
        }

        &:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4);
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        span { user-select: none; }
      }

      .profile-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .profile-name {
          font-size: 20px;
          font-weight: 600;
          color: var(--color-text-primary, #303133);
          transition: color 0.3s ease;
          word-break: break-word;
          
          @media (max-width: 768px) {
            font-size: 18px;
          }
          
          @media (max-width: 480px) {
            font-size: 16px;
          }
        }

        .profile-phone {
          font-size: 14px;
          color: var(--color-text-regular, #606266);
          transition: color 0.3s ease;
          word-break: break-all;
          
          @media (max-width: 768px) {
            font-size: 13px;
          }
          
          @media (max-width: 480px) {
            font-size: 12px;
          }
        }
      }
    }
  }

  .settings-card {
    background: var(--bg-color-white, #ffffff);
    border-radius: 12px;
    padding: 16px 0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 16px;
    transition: background-color 0.3s ease;

    @media (max-width: 768px) {
      margin-bottom: 12px;
      border-radius: 10px;
    }
    
    @media (max-width: 480px) {
      margin-bottom: 10px;
      border-radius: 8px;
    }
    
    html.dark & {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
    }

    .card-section-title {
      padding: 0 20px 12px;
      font-size: 14px;
      font-weight: 600;
      color: var(--color-text-regular, #606266);
      border-bottom: 1px solid var(--border-color-light, #e4e7ed);
      margin-bottom: 8px;
      transition: color 0.3s ease;
      
      @media (max-width: 768px) {
        padding: 0 16px 10px;
        font-size: 13px;
      }
      
      @media (max-width: 480px) {
        padding: 0 14px 8px;
        font-size: 12px;
      }
      
      html.dark & {
        color: var(--color-text-secondary, #cbd5e0);
        border-bottom-color: var(--border-color-light, #4a5568);
      }
    }

    .menu-list {
      .menu-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        cursor: pointer;
        transition: all 0.3s ease;
        user-select: none;
        -webkit-tap-highlight-color: transparent;
        min-height: 56px; /* Better touch target for mobile */

        @media (max-width: 768px) {
          padding: 14px 16px;
          min-height: 52px;
        }
        
        @media (max-width: 480px) {
          padding: 12px 14px;
          min-height: 48px;
        }

        &:hover {
          background-color: var(--bg-color-light, #f5f7fa);
          
          html.dark & {
            background-color: var(--border-color-light, #4a5568);
          }
        }

        &:active {
          background-color: var(--border-color-lighter, #f0f2f5);
        }

        .menu-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 0;

          @media (max-width: 768px) {
            gap: 10px;
          }

          .menu-icon {
            font-size: 18px;
            color: var(--el-color-primary, #409eff);
            flex-shrink: 0;
            
            @media (max-width: 768px) {
              font-size: 16px;
            }
            
            @media (max-width: 480px) {
              font-size: 15px;
            }
          }

          .menu-label {
            font-size: 15px;
            color: var(--color-text-primary, #303133);
            transition: color 0.3s ease;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            
            @media (max-width: 768px) {
              font-size: 14px;
            }
            
            @media (max-width: 480px) {
              font-size: 13px;
            }
          }
        }

        .menu-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
          
          @media (max-width: 768px) {
            gap: 6px;
          }
        }

        .menu-arrow {
          font-size: 16px;
          color: var(--color-text-secondary, #909399);
          transition: color 0.3s ease;
          flex-shrink: 0;
          
          @media (max-width: 768px) {
            font-size: 14px;
          }
          
          @media (max-width: 480px) {
            font-size: 13px;
          }
        }
      }
    }
  }

  .logout-section {
    padding: 0 8px 16px;

    @media (max-width: 768px) {
      padding: 0 4px 12px;
    }
    
    @media (max-width: 480px) {
      padding: 0 2px 10px;
    }

    .logout-btn {
      width: 100%;
      height: 48px;
      font-size: 16px;
      font-weight: 500;
      border-radius: 12px;
      background: linear-gradient(135deg, #f56c6c 0%, #e74c3c 100%);
      border: none;
      box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3);
      transition: all 0.3s ease;
      min-height: 48px; /* Better touch target */

      @media (max-width: 768px) {
        height: 44px;
        font-size: 15px;
        border-radius: 10px;
        min-height: 44px;
      }
      
      @media (max-width: 480px) {
        height: 42px;
        font-size: 14px;
        border-radius: 8px;
        min-height: 42px;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(245, 108, 108, 0.4);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .version-text {
    text-align: center;
    font-size: 13px;
    color: var(--color-text-secondary, #909399);
    padding: 16px;
    margin-top: 8px;
    transition: color 0.3s ease;
    
    @media (max-width: 768px) {
      font-size: 12px;
      padding: 12px;
    }
    
    @media (max-width: 480px) {
      font-size: 11px;
      padding: 10px;
    }
  }

  .wechat-dialog-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;

    @media (max-width: 768px) {
      padding: 16px 0;
    }
    
    @media (max-width: 480px) {
      padding: 12px 0;
    }
    
    html.dark & {
      color: var(--color-text-primary, #f7fafc);
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 40px 0;

      @media (max-width: 768px) {
        padding: 30px 0;
        gap: 10px;
      }
      
      @media (max-width: 480px) {
        padding: 24px 0;
        gap: 8px;
      }

      .loading-icon {
        font-size: 32px;
        color: var(--el-color-primary, #409eff);
        animation: rotate 1s linear infinite;
        
        @media (max-width: 768px) {
          font-size: 28px;
        }
        
        @media (max-width: 480px) {
          font-size: 24px;
        }
      }

      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    }

    .qr-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;

      @media (max-width: 768px) {
        gap: 12px;
      }
      
      @media (max-width: 480px) {
        gap: 10px;
      }

      .qr-code {
        width: 250px;
        height: 250px;
        border: 1px solid var(--border-color-light, #e4e7ed);
        border-radius: 8px;
        padding: 8px;
        background: var(--bg-color-white, #fff);
        transition: all 0.3s ease;
        max-width: 100%; /* Ensure it doesn't overflow on small screens */
        
        @media (max-width: 768px) {
          width: 200px;
          height: 200px;
        }
        
        @media (max-width: 480px) {
          width: 180px;
          height: 180px;
          padding: 6px;
        }
        
        html.dark & {
          filter: invert(0);
          background: #fff;
        }
      }

      .qr-tip { 
        color: var(--color-text-secondary, #909399);
        
        html.dark & {
          color: var(--color-text-secondary, #a0aec0);
        }
      }
    }

    .error-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 20px 0;

      @media (max-width: 768px) {
        padding: 16px 0;
        gap: 10px;
      }
      
      @media (max-width: 480px) {
        padding: 12px 0;
        gap: 8px;
      }

      .error-icon {
        font-size: 32px;
        color: var(--el-color-danger, #f56c6c);
        
        @media (max-width: 768px) {
          font-size: 28px;
        }
        
        @media (max-width: 480px) {
          font-size: 24px;
        }
      }

      .retry-btn {
        margin-top: 8px;
      }
    }

    .bind-success {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 16px;
      background: var(--bg-color-base, #f0f9ff);
      border-radius: 8px;
      margin-top: 16px;
      transition: background-color 0.3s ease;

      @media (max-width: 768px) {
        padding: 12px;
        gap: 6px;
        margin-top: 12px;
      }
      
      @media (max-width: 480px) {
        padding: 10px;
        gap: 5px;
        margin-top: 10px;
        border-radius: 6px;
      }

      .success-icon { color: var(--el-color-success, #67c23a); }

      span {
        color: var(--el-color-success, #67c23a);
        font-weight: 500;
      }
    }
  }
}
</style>
